import { portalLogout } from '@api/portalServices';
import { type SessionState, useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useQueryClient } from '@tanstack/react-query';
import { getIdpLookupUrl, getSSOLogoutUrl } from '@utils/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  resetMfaEnrollmentBanner,
  resetPasswordExpirationBanner,
  resetTrialEndedBanner,
} from '@/common/utils/sessionStorage';

const useLogout = () => {
  const { tenantId, clearSession, refreshSession } = useSession();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    resetPasswordExpirationBanner();
    resetMfaEnrollmentBanner();
    resetTrialEndedBanner();
    if (tenantId) {
      await refreshSession();
    }

    const session = queryClient.getQueryData<SessionState | null>([QueryKey.CLIENT_USER_SESSION]);

    queryClient.clear();

    if (session?.tenantId) {
      const workflowId = uuidv4();
      globalThis.location.href = getSSOLogoutUrl(session.tenantId, workflowId);
      return;
    }

    clearSession();
    globalThis.location.href = getIdpLookupUrl();
  };
  return handleLogout;
};

const useLocalLogout = () => {
  const { tenantId, clearSession } = useSession();
  const { emitError } = useErrorContext();
  const queryClient = useQueryClient();

  return async () =>
    portalLogout(tenantId || '')
      .then(() => {
        resetPasswordExpirationBanner();
        resetMfaEnrollmentBanner();
        resetTrialEndedBanner();
        queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_USER_SESSION], exact: true });
        queryClient.clear();
        clearSession();
      })
      .catch(e => {
        console.error('Error logging out:', e);
        emitError(new AppError(e));
      });
};

export { useLogout, useLocalLogout };
